import type { FormType, FormField } from '@/types/form';

const STORAGE_KEY = 'admin-forms';

export interface StorageError extends Error {
  code: 'STORAGE_ERROR' | 'PARSE_ERROR' | 'NOT_FOUND';
}

class FormStorage {
  private createError(message: string, code: StorageError['code']): StorageError {
    const error = new Error(message) as StorageError;
    error.code = code;
    return error;
  }

  private getStorageData(): Record<string, FormType> {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error parsing forms from localStorage:', error);
      throw this.createError('Failed to parse stored forms', 'PARSE_ERROR');
    }
  }

  private saveStorageData(data: Record<string, FormType>): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving forms to localStorage:', error);
      throw this.createError('Failed to save forms', 'STORAGE_ERROR');
    }
  }

  // Get all forms
  getAllForms(): FormType[] {
    try {
      const data = this.getStorageData();
      return Object.values(data).sort((a, b) => 
        new Date(b.metadata.updatedAt).getTime() - new Date(a.metadata.updatedAt).getTime()
      );
    } catch (error) {
      console.error('Error getting all forms:', error);
      return [];
    }
  }

  // Get form by ID
  getFormById(id: string): FormType | null {
    try {
      const data = this.getStorageData();
      return data[id] || null;
    } catch (error) {
      console.error(`Error getting form ${id}:`, error);
      return null;
    }
  }

  // Get forms by category
  getFormsByCategory(category: FormType['category']): FormType[] {
    try {
      return this.getAllForms().filter(form => form.category === category);
    } catch (error) {
      console.error(`Error getting forms by category ${category}:`, error);
      return [];
    }
  }

  // Get forms by status
  getFormsByStatus(status: FormType['status']): FormType[] {
    try {
      return this.getAllForms().filter(form => form.status === status);
    } catch (error) {
      console.error(`Error getting forms by status ${status}:`, error);
      return [];
    }
  }

  // Save form (create or update)
  saveForm(form: FormType): void {
    try {
      const data = this.getStorageData();
      const now = new Date().toISOString();
      
      const updatedForm: FormType = {
        ...form,
        metadata: {
          ...form.metadata,
          updatedAt: now,
          createdAt: data[form.id]?.metadata.createdAt || now,
        },
      };

      data[form.id] = updatedForm;
      this.saveStorageData(data);
    } catch (error) {
      console.error('Error saving form:', error);
      throw error;
    }
  }

  // Delete form
  deleteForm(id: string): void {
    try {
      const data = this.getStorageData();
      if (!data[id]) {
        throw this.createError(`Form with id ${id} not found`, 'NOT_FOUND');
      }
      
      delete data[id];
      this.saveStorageData(data);
    } catch (error) {
      console.error(`Error deleting form ${id}:`, error);
      throw error;
    }
  }

  // Duplicate form
  duplicateForm(id: string): FormType {
    try {
      const originalForm = this.getFormById(id);
      if (!originalForm) {
        throw this.createError(`Form with id ${id} not found`, 'NOT_FOUND');
      }

      const newId = this.generateId();
      const now = new Date().toISOString();
      
      const duplicatedForm: FormType = {
        ...originalForm,
        id: newId,
        name: `${originalForm.name} (Copy)`,
        status: 'draft',
        version: '1.0',
        metadata: {
          ...originalForm.metadata,
          createdAt: now,
          updatedAt: now,
        },
        fields: originalForm.fields.map(field => ({
          ...field,
          id: this.generateFieldId(),
        })),
      };

      this.saveForm(duplicatedForm);
      return duplicatedForm;
    } catch (error) {
      console.error(`Error duplicating form ${id}:`, error);
      throw error;
    }
  }

  // Add field to form
  addField(formId: string, field: Omit<FormField, 'id' | 'sortOrder'>): FormField {
    try {
      const form = this.getFormById(formId);
      if (!form) {
        throw this.createError(`Form with id ${formId} not found`, 'NOT_FOUND');
      }

      const newField: FormField = {
        ...field,
        id: this.generateFieldId(),
        sortOrder: form.fields.length,
      };

      const updatedForm: FormType = {
        ...form,
        fields: [...form.fields, newField],
      };

      this.saveForm(updatedForm);
      return newField;
    } catch (error) {
      console.error(`Error adding field to form ${formId}:`, error);
      throw error;
    }
  }

  // Update field in form
  updateField(formId: string, fieldId: string, updates: Partial<FormField>): void {
    try {
      const form = this.getFormById(formId);
      if (!form) {
        throw this.createError(`Form with id ${formId} not found`, 'NOT_FOUND');
      }

      const fieldIndex = form.fields.findIndex(field => field.id === fieldId);
      if (fieldIndex === -1) {
        throw this.createError(`Field with id ${fieldId} not found`, 'NOT_FOUND');
      }

      const updatedFields = [...form.fields];
      updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...updates };

      const updatedForm: FormType = {
        ...form,
        fields: updatedFields,
      };

      this.saveForm(updatedForm);
    } catch (error) {
      console.error(`Error updating field ${fieldId} in form ${formId}:`, error);
      throw error;
    }
  }

  // Delete field from form
  deleteField(formId: string, fieldId: string): void {
    try {
      const form = this.getFormById(formId);
      if (!form) {
        throw this.createError(`Form with id ${formId} not found`, 'NOT_FOUND');
      }

      const updatedFields = form.fields
        .filter(field => field.id !== fieldId)
        .map((field, index) => ({ ...field, sortOrder: index }));

      const updatedForm: FormType = {
        ...form,
        fields: updatedFields,
      };

      this.saveForm(updatedForm);
    } catch (error) {
      console.error(`Error deleting field ${fieldId} from form ${formId}:`, error);
      throw error;
    }
  }

  // Reorder fields in form
  reorderFields(formId: string, fieldIds: string[]): void {
    try {
      const form = this.getFormById(formId);
      if (!form) {
        throw this.createError(`Form with id ${formId} not found`, 'NOT_FOUND');
      }

      const fieldMap = new Map(form.fields.map(field => [field.id, field]));
      const reorderedFields = fieldIds.map((fieldId, index) => {
        const field = fieldMap.get(fieldId);
        if (!field) {
          throw this.createError(`Field with id ${fieldId} not found`, 'NOT_FOUND');
        }
        return { ...field, sortOrder: index };
      });

      const updatedForm: FormType = {
        ...form,
        fields: reorderedFields,
      };

      this.saveForm(updatedForm);
    } catch (error) {
      console.error(`Error reordering fields in form ${formId}:`, error);
      throw error;
    }
  }

  // Clear all forms
  clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing all forms:', error);
      throw this.createError('Failed to clear all forms', 'STORAGE_ERROR');
    }
  }

  // Export forms
  exportForms(): string {
    try {
      const data = this.getStorageData();
      return JSON.stringify(data, null, 2);
    } catch (error) {
      console.error('Error exporting forms:', error);
      throw error;
    }
  }

  // Import forms
  importForms(jsonData: string): void {
    try {
      const importedData = JSON.parse(jsonData);
      if (typeof importedData !== 'object' || importedData === null) {
        throw new Error('Invalid import data format');
      }
      
      this.saveStorageData(importedData);
    } catch (error) {
      console.error('Error importing forms:', error);
      throw this.createError('Failed to import forms', 'PARSE_ERROR');
    }
  }

  // Utility methods
  private generateId(): string {
    return 'form-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  private generateFieldId(): string {
    return 'field-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  // Get storage statistics
  getStorageStats(): { 
    totalForms: number; 
    totalFields: number; 
    storageSize: number;
    byCategory: Record<string, number>;
    byStatus: Record<string, number>;
  } {
    try {
      const forms = this.getAllForms();
      const totalFields = forms.reduce((sum, form) => sum + form.fields.length, 0);
      const storageSize = new Blob([this.exportForms()]).size;
      
      const byCategory = forms.reduce((acc, form) => {
        acc[form.category] = (acc[form.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const byStatus = forms.reduce((acc, form) => {
        acc[form.status] = (acc[form.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return {
        totalForms: forms.length,
        totalFields,
        storageSize,
        byCategory,
        byStatus,
      };
    } catch (error) {
      console.error('Error getting storage stats:', error);
      return {
        totalForms: 0,
        totalFields: 0,
        storageSize: 0,
        byCategory: {},
        byStatus: {},
      };
    }
  }
}

export const formStorage = new FormStorage();